import React, { useState } from 'react';
import styled from 'styled-components';
import ErrorCard from '../ErrorCard';

const Container = styled.div`
  margin: 32px 0;
`;

const Input = styled.input`
  width: 100%;
  /* box-sizing: border-box ensures padding is included in the declared width,
     fixing the narrower-than-container issue on mobile Safari and Chrome. */
  box-sizing: border-box;
  padding: 16px 20px;
  border: 2px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-sm, 6px);
  font-size: 1rem;
  font-family: var(--zyvgo-font-family);
  color: var(--zyvgo-text-primary, #222222);
  background-color: #FFFFFF;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  /* Remove native date-input chrome on iOS Safari/Chrome so our styles apply fully */
  &[type="date"] {
    -webkit-appearance: none;
    appearance: none;
    /* Prevent Safari from collapsing the input to its intrinsic content width */
    min-width: 0;
    display: block;
  }
  
  &:focus {
    outline: none;
    border-color: var(--zyvgo-primary, #222222);
    box-shadow: 0 0 0 3px rgba(34, 34, 34, 0.1);
  }
  
  &::placeholder {
    color: #999999;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  min-height: 140px;
  padding: 16px 20px;
  border: 2px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-sm, 6px);
  font-size: 1rem;
  resize: vertical;
  font-family: var(--zyvgo-font-family);
  color: var(--zyvgo-text-primary, #222222);
  background-color: #FFFFFF;
  line-height: 1.5;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--zyvgo-primary, #222222);
    box-shadow: 0 0 0 3px rgba(34, 34, 34, 0.1);
  }
  
  &::placeholder {
    color: #999999;
  }
`;

const ErrorMessage = styled.div`
  color: var(--zyvgo-error, #e74c3c);
  font-size: 0.875rem;
  margin-top: 12px;
  font-weight: 500;
`;

const TextInput = ({ question, value, onChange, error, hideLabel = false, onAgeError, onBlur, disabled, questionnaire }) => {
  const isTextArea = question.question_type === 'textarea';
  const isDateOfBirth = question.question_type === 'date' &&
    (
      question.beluga_field_mapping === 'date_of_birth' ||
      question.beluga_field_mapping === 'dob' ||
      (question.question_text || '').toLowerCase().includes('date of birth')
    );

  const rawMinAge =
    question?.validation?.min_age ??
    questionnaire?.min_age ??
    questionnaire?.settings?.min_age ??
    questionnaire?.dob_min_age;
  const parsedMinAge = Number.parseInt(rawMinAge, 10);
  const effectiveMinAge = Number.isFinite(parsedMinAge) && parsedMinAge > 0 ? parsedMinAge : 18;

  // ─── Pure helpers (no state, usable in lazy initializers) ────────────────────

  const _calcAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob + (dob.includes('T') ? '' : 'T00:00:00'));
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const _getYearError = (dob) => {
    if (!dob) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob) && !/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
      return 'Please enter a complete date of birth';
    }
    let year;
    let month;
    let day;
    if (dob.includes('-')) [year, month, day] = dob.split('-').map(Number);
    else if (dob.includes('/')) [month, day, year] = dob.split('/').map(Number);
    else return 'Invalid date format';
    if (isNaN(year)) return 'Please enter a valid date of birth';
    const parsedDate = new Date(year, month - 1, day);
    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) return 'Please enter a valid date of birth';
    if (year < 1920) return 'Please enter a realistic date.';
    // NOTE: Do NOT check year > maxYear here — that would show a technical
    // "born in XXXX or earlier" message. The age eligibility check below
    // handles under-age cases with the correct user-facing message.
    return null;
  };

  const _getAgeError = (dob) => {
    if (!isDateOfBirth || !dob) return null;
    const yearErr = _getYearError(dob);
    if (yearErr) return yearErr;
    const age = _calcAge(dob);
    if (age !== null && age < effectiveMinAge) {
      return `You must be at least ${effectiveMinAge} years old to continue.`;
    }
    return null;
  };

  // ─── State (all hooks together at the top) ────────────────────────────────────

  // Lazy-initialize ageError from the existing value so the ErrorCard renders
  // immediately when the user navigates back to this step with a pre-filled value.
  const [ageError, setAgeError] = useState(() => _getAgeError(value));

  // hasTouched gates the visual ErrorCard.
  // true  → field had a value on mount (back-nav): show card right away.
  // false → fresh visit (empty field / Safari): wait for first blur.
  const [hasTouched, setHasTouched] = useState(() => isDateOfBirth && !!value);

  // ─── Propagate initial error to parent exactly once on mount ──────────────────
  const didNotifyMount = React.useRef(false);
  if (!didNotifyMount.current && isDateOfBirth && value && onAgeError) {
    didNotifyMount.current = true;
    onAgeError(_getAgeError(value));
  }

  // ─── Validation helpers (use state setters) ───────────────────────────────────

  const calculateAge = _calcAge;

  const checkAgeEligibility = (dob) => {
    const age = calculateAge(dob);
    if (age === null) return { eligible: true };
    if (effectiveMinAge > 0 && age < effectiveMinAge) {
      return { eligible: false, message: `You must be at least ${effectiveMinAge} years old to continue.` };
    }
    return { eligible: true };
  };

  const validateYear = (dob) => {
    const err = _getYearError(dob);
    if (err) return { valid: false, message: err };
    return { valid: true };
  };

  const runValidation = (currentValue) => {
    if (isDateOfBirth && currentValue) {
      const msg = _getAgeError(currentValue);
      setAgeError(msg);
      if (onAgeError) onAgeError(msg);
    } else if (isDateOfBirth && !currentValue) {
      setAgeError(null);
      if (onAgeError) onAgeError(null);
    }
  };

  const handleChange = (inputValue) => {
    // Keep the parent Continue-button state in sync while DOB is being edited.
    if (isDateOfBirth) {
      runValidation(inputValue);
    }
    onChange(inputValue);
  };

  // Handle blur event
  const handleBlur = (e) => {
    setHasTouched(true);
    runValidation(value);
    if (onBlur) {
      onBlur(e);
    }
  };

  // Determine input type based on question type
  const getInputType = () => {
    switch (question.question_type) {
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      case 'date':
        return 'date';
      case 'number':
        return 'number';
      default:
        return 'text';
    }
  };

  // Placeholder from the question's type and field mapping only. Matching words
  // in the question text put address hints on clinical questions ("units" →
  // "Apt 4B", "toxicity" → "San Francisco"). Address groups render through
  // GroupedQuestion, which has its own placeholders.
  const getPlaceholder = () => {
    const mapping = (question.beluga_field_mapping || '').toLowerCase();
    const type = (question.question_type || '').toLowerCase();

    if (type === 'email' || mapping === 'email') {
      return 'your.email@example.com';
    }
    if (type === 'phone' || mapping === 'phone' || mapping === 'phone_number') {
      return '(321) 123-4567';
    }
    if (type === 'state' || mapping === 'state') {
      return 'CA';
    }
    if (type === 'zip' || mapping === 'zip' || mapping === 'zip_code') {
      return '94103';
    }
    if (mapping === 'city') {
      return 'San Francisco';
    }
    if (mapping === 'address' || mapping === 'street' || mapping === 'street_address') {
      return '1240 Mission St';
    }
    if (mapping === 'apt' || mapping === 'address2' || mapping === 'address_line_2') {
      return 'Apt 4B (optional)';
    }
    if (mapping === 'country') {
      return 'United States';
    }
    return 'Your Response';
  };

  // Get max length based on question type
  const getMaxLength = () => {
    if (question.validation_rules?.max_length) {
      return question.validation_rules.max_length;
    }

    switch (question.question_type) {
      case 'phone':
        return 14; // Allow for formatting
      case 'state':
        return 2;
      case 'zip':
        return 5;
      case 'date':
        return 10; // YYYY-MM-DD format
      default:
        return undefined;
    }
  };

  // Get min date for date inputs (to ensure valid 4-digit year)
  const getMinDate = () => {
    if (isDateOfBirth) {
      // No one alive would be born before 1900
      return '1900-01-01';
    }
    return undefined;
  };

  // Get max date for date inputs
  const getMaxDate = () => {
    if (isDateOfBirth) {
      const maxDate = new Date();
      if (effectiveMinAge !== undefined && effectiveMinAge > 0) {
        maxDate.setFullYear(maxDate.getFullYear() - effectiveMinAge);
      }
      return maxDate.toISOString().split('T')[0];
    }
    return undefined;
  };

  return (
    <Container>
      {isTextArea ? (
        <TextArea
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={getPlaceholder()}
          maxLength={getMaxLength()}
          disabled={disabled}
        />
      ) : (
        <Input
          type={getInputType()}
          value={value || ''}
          onChange={(e) => {
            const inputValue = e.target.value;
            if (question.question_type === 'date') {
              const parts = inputValue.split('-');
              if (parts[0] && parts[0].length > 4) {
                parts[0] = parts[0].slice(0, 4);
              }
              handleChange(parts.join('-'));
              return;
            }
            handleChange(inputValue);
          }}
          onBlur={handleBlur}
          placeholder={getPlaceholder()}
          maxLength={getMaxLength()}
          min={getMinDate()}
          max={getMaxDate()}
          disabled={disabled}
        />
      )}

      {/* Age Eligibility Error for DOB — only shown after the user has interacted (blurred) the field */}
      {ageError && hasTouched && (
        <ErrorCard
          title="You do not meet the minimum age requirement"
          message={ageError}
          variant="warning"
        />
      )}

      {error && !ageError && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};

export default TextInput;
